// History.tsx - Updated to work with Firebase Realtime Database

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { getAnalysisHistory, StoredAnalysisResult, getFSChecklistHistory, FSChecklistResult } from "@/lib/firebaseStorageService";
import { format } from "date-fns";
import { Timestamp } from "firebase/firestore";
import { Button } from "@/components/ui/button";
import { FileText, AlertCircle, ClipboardCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { secureLog } from "@/lib/securityUtils";
import DashboardTopbar from "@/components/layout/DashboardTopbar";

// Helper function to safely format timestamps
const formatTimestamp = (timestamp: any, formatString: string = "MMM d, yyyy 'at' h:mm a"): string => {
  try {
    if (!timestamp) {
      console.warn("formatTimestamp: timestamp is null or undefined");
      return "Unknown date";
    }

    let date: Date;

    // Handle Firestore Timestamp instances (must check first)
    if (timestamp instanceof Timestamp) {
      date = timestamp.toDate();
    }
    // Handle Date instances
    else if (timestamp instanceof Date) {
      date = timestamp;
    }
    // Handle Firestore timestamp objects (from serialized data)
    // IMPORTANT: Check for seconds property BEFORE checking _methodName
    else if (typeof timestamp === 'object' && timestamp !== null) {
      // Check for Firestore timestamp format FIRST (most common case)
      if (timestamp.seconds !== undefined && typeof timestamp.seconds === 'number') {
        // Firestore timestamp object format: {seconds: number, nanoseconds: number}
        date = new Date(timestamp.seconds * 1000 + (timestamp.nanoseconds || 0) / 1000000);
      } else if (timestamp._seconds !== undefined && typeof timestamp._seconds === 'number') {
        // Alternative Firestore format
        date = new Date(timestamp._seconds * 1000 + (timestamp._nanoseconds || 0) / 1000000);
      } else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        // Object with toDate method (Firestore Timestamp-like)
        try {
          date = timestamp.toDate();
        } catch (e) {
          console.error("formatTimestamp: Error calling toDate()", e);
          // Fallback to checking for seconds
          if (timestamp.seconds !== undefined) {
            date = new Date(timestamp.seconds * 1000);
          } else {
            throw new Error("Cannot convert timestamp to date");
          }
        }
      }
      // Only check for unresolved serverTimestamp if it's truly unresolved (no seconds property)
      // This should rarely happen with properly stored data
      else if (timestamp._methodName === 'serverTimestamp' && !timestamp.seconds && !timestamp._seconds) {
        console.warn("formatTimestamp: Detected unresolved serverTimestamp - this should not happen with stored data");
        // Return "Unknown date" instead of using current date to avoid showing wrong dates
        return "Unknown date";
      } else {
        // Try to convert as-is - might be a Date-like object or other format
        date = new Date(timestamp);
      }
    }
    // Handle string/number timestamps
    else if (typeof timestamp === 'string' || typeof timestamp === 'number') {
      date = new Date(timestamp);
    }
    // Fallback
    else {
      console.warn("formatTimestamp: Unknown timestamp type:", typeof timestamp, timestamp);
      date = new Date(timestamp);
    }

    // Check if the date is valid
    if (isNaN(date.getTime())) {
      console.error("formatTimestamp: Invalid date result:", date, "from timestamp:", timestamp, "type:", typeof timestamp);
      // Try to extract more information for debugging
      if (timestamp && typeof timestamp === 'object') {
        console.error("formatTimestamp: Timestamp object keys:", Object.keys(timestamp));
        console.error("formatTimestamp: Timestamp object:", JSON.stringify(timestamp, null, 2));
      }
      return "Invalid date";
    }

    return format(date, formatString);
  } catch (error) {
    console.error("formatTimestamp: Error formatting date:", error, "timestamp:", timestamp, "type:", typeof timestamp);
    // Try one more fallback - check if it's a number that can be converted
    if (typeof timestamp === 'number' || (typeof timestamp === 'string' && !isNaN(Number(timestamp)))) {
      try {
        const fallbackDate = new Date(Number(timestamp));
        if (!isNaN(fallbackDate.getTime())) {
          return format(fallbackDate, formatString);
        }
      } catch (e) {
        // Ignore fallback error
      }
    }
    return "Invalid date";
  }
};

const History = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);

  const {
    data: analysisHistory,
    isLoading: isLoadingAnalysis,
    error: queryError,
    refetch
  } = useQuery({
    queryKey: ["analysisHistory", currentUser?.uid],
    queryFn: async () => {
      if (!currentUser) return [];

      try {
        secureLog("Fetching analysis history for user:", { userId: '[REDACTED]' });
        const history = await getAnalysisHistory(currentUser.uid);
        secureLog("Fetched history:", { count: history.length });
        console.log("Retrieved analysis history:", history);
        return history;
      } catch (err) {
        secureLog("Error fetching analysis history:", err);
        throw new Error(`Error fetching analysis history: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
    enabled: !!currentUser,
    retry: 2,
    staleTime: 30000, // 30 seconds
  });

  const {
    data: fsChecklistHistory,
    isLoading: isLoadingChecklists,
    error: checklistError,
    refetch: refetchChecklists
  } = useQuery({
    queryKey: ["fsChecklistHistory", currentUser?.uid],
    queryFn: async () => {
      if (!currentUser) return [];

      try {
        secureLog("Fetching FS Checklist history for user:", { userId: '[REDACTED]' });
        const history = await getFSChecklistHistory(currentUser.uid);
        secureLog("Fetched FS Checklist history:", { count: history.length });
        return history;
      } catch (err) {
        secureLog("Error fetching FS Checklist history:", err);
        throw new Error(`Error fetching FS Checklist history: ${err instanceof Error ? err.message : String(err)}`);
      }
    },
    enabled: !!currentUser,
    retry: 2,
    staleTime: 30000,
  });

  const isLoading = isLoadingAnalysis || isLoadingChecklists;
  const allHistory: Array<{ type: 'analysis' | 'checklist', data: StoredAnalysisResult | FSChecklistResult }> = [];

  if (analysisHistory) {
    allHistory.push(...analysisHistory.map(item => ({ type: 'analysis' as const, data: item })));
  }
  if (fsChecklistHistory) {
    allHistory.push(...fsChecklistHistory.map(item => ({ type: 'checklist' as const, data: item })));
  }

  // Sort by createdAt (most recent first, broken timestamps pushed to bottom)
  allHistory.sort((a, b) => {
    const getTime = (ts: any): number => {
      if (!ts) return 0;
      if (ts instanceof Timestamp) return ts.toMillis();
      if (ts instanceof Date) return ts.getTime();
      if (typeof ts === 'number') return ts;
      if (typeof ts === 'object' && typeof ts.seconds === 'number') return ts.seconds * 1000;
      if (typeof ts === 'object' && typeof ts._seconds === 'number') return ts._seconds * 1000;
      return 0;
    };
    return getTime(b.data.createdAt) - getTime(a.data.createdAt);
  });

  useEffect(() => {
    if (queryError) {
      secureLog("Query error:", queryError);
      setError(`Error loading history: ${queryError instanceof Error ? queryError.message : String(queryError)}`);
    }
  }, [queryError]);

  const handleViewDetails = (id: string, type: 'analysis' | 'checklist') => {
    if (type === 'analysis') {
      secureLog("Navigating to analysis details:", { id });
      navigate(`/dashboard/analysis/${id}`);
    } else {
      secureLog("Navigating to FS Checklist details:", { id });
      navigate(`/dashboard/fs-checklist/${id}`);
    }
  };

  const getComplianceColor = (percentage: number) => {
    if (percentage >= 80) return "text-green-600";
    if (percentage >= 60) return "text-yellow-600";
    return "text-red-600";
  };

  const getComplianceBadgeColor = (percentage: number) => {
    if (percentage >= 80) return "bg-green-100 text-green-800";
    if (percentage >= 60) return "bg-yellow-100 text-yellow-800";
    return "bg-red-100 text-red-800";
  };

  const refreshHistory = () => {
    refetch();
    refetchChecklists();
    toast({
      title: "Refreshing",
      description: "History is being refreshed...",
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <DashboardTopbar />
      <div className="pt-20 px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold">Analysis History</h1>
            <div className="flex space-x-2">
              <Button variant="outline" onClick={refreshHistory}>
                Refresh
              </Button>
              <Button onClick={() => navigate("/dashboard/Results-Analyzer")}>
                New Analysis
              </Button>
            </div>
          </div>

          {error && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : allHistory.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium mb-2">No history</h3>
              <p className="text-gray-500 mb-6">
                You haven't performed any analysis or submitted any checklists yet.
              </p>
              <div className="flex gap-4 justify-center">
                <Button onClick={() => navigate("/dashboard/Results-Analyzer")}>
                  Start New Analysis
                </Button>
                <Button variant="outline" onClick={() => navigate("/dashboard/fs-checklist")}>
                  Submit FS Checklist
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {allHistory.map((item) => {
                if (item.type === 'analysis') {
                  const analysis = item.data as StoredAnalysisResult;
                  return (
                    <div
                      key={`analysis-${analysis.id}`}
                      className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => handleViewDetails(analysis.id, 'analysis')}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-semibold mb-2">
                            {analysis.companyName?.replace(/QQ(\d)/g, 'Q$1')}
                          </h3>
                          <p className="text-gray-500 text-sm">
                            Analyzed on {formatTimestamp(analysis.createdAt || analysis.timestamp)}
                          </p>
                          <div className="mt-2 text-sm text-gray-600">
                            <span className="inline-block mr-4">
                              Report Type: {analysis.parameters?.audit_report_type || "Standard"}
                            </span>
                            {analysis.parameters?.is_nbfc === "Yes" && (
                              <span className="inline-block mr-4 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                                NBFC
                              </span>
                            )}
                            {analysis.parameters?.company_listing_status === "Listed" && (
                              <span className="inline-block mr-4 bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs">
                                Listed
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-3xl font-bold ${getComplianceColor(analysis.summary?.compliancePercentage || 0)}`}>
                            {analysis.summary?.compliancePercentage || 0}%
                          </div>
                          <div className="text-sm text-gray-500">Compliance</div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex space-x-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-blue-600">
                              {analysis.summary?.total || 0}
                            </div>
                            <div className="text-xs text-gray-500">Total Checks</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-green-600">
                              {analysis.summary?.compliant || 0}
                            </div>
                            <div className="text-xs text-gray-500">Passed</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-red-600">
                              {analysis.summary?.nonCompliant || 0}
                            </div>
                            <div className="text-xs text-gray-500">Failed</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-gray-600">
                              {analysis.documents ? Object.keys(analysis.documents).length : 0}
                            </div>
                            <div className="text-xs text-gray-500">Documents</div>
                          </div>
                        </div>

                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${getComplianceBadgeColor(analysis.summary?.compliancePercentage || 0)}`}>
                          {analysis.summary?.compliancePercentage || 0}% Compliant
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div className="mt-4">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${(analysis.summary?.compliancePercentage || 0) >= 80
                                ? "bg-green-500"
                                : (analysis.summary?.compliancePercentage || 0) >= 60
                                  ? "bg-yellow-500"
                                  : "bg-red-500"
                              }`}
                            style={{ width: `${analysis.summary?.compliancePercentage || 0}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  );
                } else {
                  const checklist = item.data as FSChecklistResult;
                  const yesCount = checklist.responses.filter(r => r.response === 'yes').length;
                  const noCount = checklist.responses.filter(r => r.response === 'no').length;
                  const naCount = checklist.responses.filter(r => r.response === 'na').length;
                  const totalResponses = checklist.responses.filter(r => r.response !== '').length;

                  return (
                    <div
                      key={`checklist-${checklist.id}`}
                      className="bg-white p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow cursor-pointer border-l-4 border-l-blue-500"
                      onClick={() => handleViewDetails(checklist.id, 'checklist')}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-start gap-3">
                          <ClipboardCheck className="h-6 w-6 text-blue-500 mt-1" />
                          <div>
                            <h3 className="text-xl font-semibold mb-2">
                              {checklist.companyName}
                            </h3>
                            <p className="text-gray-500 text-sm">
                              Submitted on {formatTimestamp(checklist.createdAt)}
                            </p>
                            <div className="mt-2 text-sm text-gray-600">
                              <span className="inline-block mr-4">
                                Financial Year: {checklist.financialYear}
                              </span>
                              <span className="inline-block mr-4">
                                Signing Partner: {checklist.signingPartner}
                              </span>
                              <span className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                                FS Checklist
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-500">Total Items</div>
                          <div className="text-2xl font-bold text-blue-600">
                            {totalResponses}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex space-x-4">
                          <div className="text-center">
                            <div className="text-xl font-bold text-green-600">
                              {yesCount}
                            </div>
                            <div className="text-xs text-gray-500">Yes</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xl font-bold text-red-600">
                              {noCount}
                            </div>
                            <div className="text-xs text-gray-500">No</div>
                          </div>
                          <div className="text-center">
                            <div className="text-xl font-bold text-gray-600">
                              {naCount}
                            </div>
                            <div className="text-xs text-gray-500">N/A</div>
                          </div>
                        </div>
                        <div className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                          Checklist Submission
                        </div>
                      </div>
                    </div>
                  );
                }
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default History;
